import React, { useState, useEffect } from 'react';
import { AD } from './admin.constants';
import { s } from './admin.styles';
import { ADIcon } from './ADIcon';
import {
  createTool, getToolHandlers,
  AiTool, AiToolType, ToolHandlers,
} from 'core/services/admin.service';

interface CreateToolFormProps {
  onCreated: (tool: AiTool) => void;
  onCancel: () => void;
  onError: (msg: string) => void;
}

const TOOL_TYPES: AiToolType[] = ['handler', 'api', 'db', 'macro'];
const DB_OPERATIONS = ['findMany', 'findUnique', 'count'];
const HTTP_METHODS = ['GET', 'POST', 'PUT', 'DELETE'];

const selectStyle: React.CSSProperties = {
  ...(s.descInput as object),
  minHeight: 36,
  appearance: 'auto' as const,
};

const inputSmall: React.CSSProperties = {
  ...(s.descInput as object),
  minHeight: 36,
};

const inputSchemaPlaceholder = `{
  "type": "object",
  "required": ["budget_id"],
  "properties": {
    "budget_id": {
      "type": "string",
      "description": "Target budget"
    }
  }
}`;

// ── Type-specific config sub-forms ───────────────────────────────────────────

function ApiConfigForm({ value, onChange }: { value: ApiConfigState; onChange: (v: ApiConfigState) => void }) {
  const addEndpoint = () => {
    onChange({ ...value, endpoints: [...value.endpoints, { method: 'GET', path: '' }] });
  };
  const updateEndpoint = (i: number, field: string, val: string) => {
    const next = value.endpoints.map((ep, j) => j === i ? { ...ep, [field]: val } : ep);
    onChange({ ...value, endpoints: next });
  };
  const removeEndpoint = (i: number) => {
    onChange({ ...value, endpoints: value.endpoints.filter((_, j) => j !== i) });
  };

  return (
    <div style={s.smallCard}>
      <div style={s.smallCardHead}>
        <ADIcon name="link" size={14} color={AD.PURPLE_STRONG} />
        <div style={s.smallCardTitle}>{'API endpoints · ' + value.endpoints.length + '/5'}</div>
        {value.endpoints.length < 5 && (
          <span style={s.smallCardLink} onClick={addEndpoint}>+ Add</span>
        )}
      </div>
      {value.endpoints.map((ep, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '100px 1fr 28px', gap: 8, alignItems: 'center' }}>
          <select value={ep.method} onChange={e => updateEndpoint(i, 'method', e.target.value)} style={selectStyle} aria-label={'Endpoint ' + (i + 1) + ' method'}>
            {HTTP_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <input
            value={ep.path}
            onChange={e => updateEndpoint(i, 'path', e.target.value)}
            placeholder="/entries?userid={{userid}}"
            style={inputSmall}
            aria-label={'Endpoint ' + (i + 1) + ' path'}
          />
          <button style={{ ...s.iconBtn, width: 28, height: 28 }} onClick={() => removeEndpoint(i)} aria-label={'Remove endpoint ' + (i + 1)}>
            <ADIcon name="close" size={12} color={AD.RED} />
          </button>
        </div>
      ))}
      <div style={s.descBox}>
        <div style={s.descLab}>Combine strategy</div>
        <select value={value.combineStrategy} onChange={e => onChange({ ...value, combineStrategy: e.target.value })} style={selectStyle} aria-label="Combine strategy">
          <option value="array">array</option>
          <option value="merge">merge</option>
        </select>
      </div>
    </div>
  );
}

function DbConfigForm({ value, onChange, dbModels }: { value: DbConfigState; onChange: (v: DbConfigState) => void; dbModels: string[] }) {
  return (
    <div style={s.smallCard}>
      <div style={s.smallCardHead}>
        <ADIcon name="tool" size={14} color={AD.PURPLE_STRONG} />
        <div style={s.smallCardTitle}>Database query</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div style={s.descBox}>
          <div style={s.descLab}>Model</div>
          {dbModels.length > 0 ? (
            <select value={value.model} onChange={e => onChange({ ...value, model: e.target.value })} style={selectStyle} aria-label="DB model">
              <option value="">Select model...</option>
              {dbModels.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          ) : (
            <input value={value.model} onChange={e => onChange({ ...value, model: e.target.value })} placeholder="entry" style={inputSmall} aria-label="DB model" />
          )}
        </div>
        <div style={s.descBox}>
          <div style={s.descLab}>Operation</div>
          <select value={value.operation} onChange={e => onChange({ ...value, operation: e.target.value })} style={selectStyle} aria-label="DB operation">
            {DB_OPERATIONS.map(op => <option key={op} value={op}>{op}</option>)}
          </select>
        </div>
      </div>
      <div style={s.descBox}>
        <div style={s.descLab}>Where clause (JSON with {'{{param}}'} tokens)</div>
        <textarea
          value={value.where}
          onChange={e => onChange({ ...value, where: e.target.value })}
          placeholder={'{ "userId": "{{userid}}" }'}
          style={{ ...s.descInput, fontFamily: 'var(--font-mono, ui-monospace, monospace)', fontSize: 12 }}
          aria-label="DB where clause"
        />
      </div>
      <div style={s.descBox}>
        <div style={s.descLab}>Take (max rows, 0 = unlimited)</div>
        <input
          type="number"
          value={value.take}
          onChange={e => onChange({ ...value, take: e.target.value })}
          placeholder="100"
          style={inputSmall}
          aria-label="DB take limit"
        />
      </div>
    </div>
  );
}

function MacroConfigForm({ value, onChange }: { value: MacroConfigState; onChange: (v: MacroConfigState) => void }) {
  const addStep = () => {
    onChange({ steps: [...value.steps, { toolName: '', inputMapping: '{}' }] });
  };
  const updateStep = (i: number, field: string, val: string) => {
    const next = value.steps.map((step, j) => j === i ? { ...step, [field]: val } : step);
    onChange({ steps: next });
  };
  const removeStep = (i: number) => {
    onChange({ steps: value.steps.filter((_, j) => j !== i) });
  };

  return (
    <div style={s.smallCard}>
      <div style={s.smallCardHead}>
        <ADIcon name="bolt" size={14} color={AD.PURPLE_STRONG} />
        <div style={s.smallCardTitle}>{'Macro steps · ' + value.steps.length + '/10'}</div>
        {value.steps.length < 10 && (
          <span style={s.smallCardLink} onClick={addStep}>+ Add step</span>
        )}
      </div>
      {value.steps.map((step, i) => (
        <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '8px 0', borderTop: i > 0 ? '1px solid ' + AD.HAIR_SOFT : 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: AD.SLATE_LIGHT }}>{'STEP ' + (i + 1)}</span>
            <div style={{ flex: 1 }} />
            <button style={{ ...s.iconBtn, width: 24, height: 24 }} onClick={() => removeStep(i)} aria-label={'Remove step ' + (i + 1)}>
              <ADIcon name="close" size={10} color={AD.RED} />
            </button>
          </div>
          <input
            value={step.toolName}
            onChange={e => updateStep(i, 'toolName', e.target.value)}
            placeholder="tool_name_to_call"
            style={inputSmall}
            aria-label={'Step ' + (i + 1) + ' tool name'}
          />
          <textarea
            value={step.inputMapping}
            onChange={e => updateStep(i, 'inputMapping', e.target.value)}
            placeholder={'{ "budget_id": "input.budget_id" }'}
            style={{ ...s.descInput, fontFamily: 'var(--font-mono, ui-monospace, monospace)', fontSize: 12, minHeight: 60 }}
            aria-label={'Step ' + (i + 1) + ' input mapping'}
          />
        </div>
      ))}
    </div>
  );
}

// ── Config state types ───────────────────────────────────────────────────────

interface ApiEndpointState { method: string; path: string }
interface ApiConfigState { endpoints: ApiEndpointState[]; combineStrategy: string }
interface DbConfigState { model: string; operation: string; where: string; take: string }
interface MacroStepState { toolName: string; inputMapping: string }
interface MacroConfigState { steps: MacroStepState[] }

const defaultApiConfig = (): ApiConfigState => ({ endpoints: [{ method: 'GET', path: '' }], combineStrategy: 'array' });
const defaultDbConfig = (): DbConfigState => ({ model: '', operation: 'findMany', where: '{}', take: '100' });
const defaultMacroConfig = (): MacroConfigState => ({ steps: [{ toolName: '', inputMapping: '{}' }] });

// ── Main form ────────────────────────────────────────────────────────────────

export function CreateToolForm({ onCreated, onCancel, onError }: CreateToolFormProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [toolType, setToolType] = useState<AiToolType>('handler');
  const [handlerKey, setHandlerKey] = useState('');
  const [inputSchema, setInputSchema] = useState(inputSchemaPlaceholder);
  const [enabled, setEnabled] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Type-specific config state
  const [apiConfig, setApiConfig] = useState<ApiConfigState>(defaultApiConfig);
  const [dbConfig, setDbConfig] = useState<DbConfigState>(defaultDbConfig);
  const [macroConfig, setMacroConfig] = useState<MacroConfigState>(defaultMacroConfig);

  // Available options from the API
  const [handlersData, setHandlersData] = useState<ToolHandlers | null>(null);
  const [loadingHandlers, setLoadingHandlers] = useState(true);

  useEffect(() => {
    getToolHandlers()
      .then(data => {
        setHandlersData(data);
        const h = data.handlers || [];
        if (h.length > 0) setHandlerKey(h[0]);
      })
      .catch(() => {})
      .finally(() => setLoadingHandlers(false));
  }, []);

  const handlers = handlersData ? handlersData.handlers || [] : [];
  const dbModels = handlersData ? handlersData.dbModels || [] : [];

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) { onError('Tool name is required'); return; }

    let parsedSchema: Record<string, unknown>;
    try { parsedSchema = JSON.parse(inputSchema); }
    catch { onError('Invalid JSON in input schema'); return; }

    // Build type-specific config
    let config: Record<string, unknown> | null = null;
    if (toolType === 'api') {
      const emptyPaths = apiConfig.endpoints.some(ep => !ep.path.trim());
      if (emptyPaths) { onError('All API endpoints need a path'); return; }
      config = {
        endpoints: apiConfig.endpoints.map(ep => ({ method: ep.method, path: ep.path.trim() })),
        combineStrategy: apiConfig.combineStrategy,
      };
    } else if (toolType === 'db') {
      if (!dbConfig.model.trim()) { onError('DB model is required'); return; }
      let parsedWhere: Record<string, unknown> = {};
      try { parsedWhere = JSON.parse(dbConfig.where); }
      catch { onError('Invalid JSON in where clause'); return; }
      config = {
        model: dbConfig.model.trim(),
        operation: dbConfig.operation,
        where: parsedWhere,
        take: Number(dbConfig.take) || 100,
      };
    } else if (toolType === 'macro') {
      const emptySteps = macroConfig.steps.some(step => !step.toolName.trim());
      if (emptySteps) { onError('All macro steps need a tool name'); return; }
      const steps = macroConfig.steps.map(step => {
        let mapping: Record<string, string> = {};
        try { mapping = JSON.parse(step.inputMapping); }
        catch { /* will be caught by API validation */ }
        return { toolName: step.toolName.trim(), inputMapping: mapping };
      });
      config = { steps };
    }

    setSubmitting(true);
    try {
      const payload: Parameters<typeof createTool>[0] = {
        name: trimmedName,
        description: description.trim(),
        inputSchema: parsedSchema,
        type: toolType,
        enabled,
      };

      if (toolType === 'handler') {
        payload.handlerKey = handlerKey || handlers[0] || '';
      } else {
        payload.handlerKey = '__dynamic__';
        payload.config = config as any;
      }

      const res = await createTool(payload);
      onCreated(res.tool);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Create failed';
      onError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Basic fields */}
      <div style={s.smallCard}>
        <div style={s.smallCardHead}>
          <ADIcon name="plus" size={14} color={AD.PURPLE_STRONG} />
          <div style={s.smallCardTitle}>New tool</div>
        </div>

        <div style={s.descBox}>
          <div style={s.descLab}>Name</div>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="my_tool_name" style={inputSmall} aria-label="New tool name" />
        </div>

        <div style={s.descBox}>
          <div style={s.descLab}>Description (the LLM reads this)</div>
          <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="What this tool does..." style={s.descInput} aria-label="New tool description" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: toolType === 'handler' ? '1fr 1fr' : '1fr', gap: 14 }}>
          <div style={s.descBox}>
            <div style={s.descLab}>Type</div>
            <select value={toolType} onChange={e => setToolType(e.target.value as AiToolType)} style={selectStyle} aria-label="Tool type">
              {TOOL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          {toolType === 'handler' && (
            <div style={s.descBox}>
              <div style={s.descLab}>Handler key</div>
              {loadingHandlers ? (
                <div style={{ fontSize: 12, color: AD.SLATE, padding: '10px 0' }}>Loading...</div>
              ) : handlers.length > 0 ? (
                <select value={handlerKey} onChange={e => setHandlerKey(e.target.value)} style={selectStyle} aria-label="Handler key">
                  {handlers.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
              ) : (
                <input value={handlerKey} onChange={e => setHandlerKey(e.target.value)} placeholder="handlerFunctionName" style={inputSmall} aria-label="Handler key" />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Type-specific config */}
      {toolType === 'api' && <ApiConfigForm value={apiConfig} onChange={setApiConfig} />}
      {toolType === 'db' && <DbConfigForm value={dbConfig} onChange={setDbConfig} dbModels={dbModels} />}
      {toolType === 'macro' && <MacroConfigForm value={macroConfig} onChange={setMacroConfig} />}

      {/* Input schema */}
      <div style={s.editorCard}>
        <div style={s.editorTopRow}>
          <div style={s.editorTabs}>
            <span style={{ ...s.editorTab, ...s.editorTabActive }}>inputSchema</span>
          </div>
          <div style={s.editorMeta}><span>JSON Schema</span></div>
        </div>
        <textarea value={inputSchema} onChange={e => setInputSchema(e.target.value)} style={{ ...s.editorTextarea, minHeight: 180 }} aria-label="Input schema" />
      </div>

      {/* Enabled + actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: AD.INK_700, cursor: 'pointer' }}>
          <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} aria-label="Enabled" />
          Create as published (enabled)
        </label>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button style={s.ghostBtn} onClick={onCancel}>Cancel</button>
          <button style={s.primaryBtn} onClick={handleSubmit} disabled={submitting} aria-label="Create tool">
            <ADIcon name="check" size={12} color="#fff" />
            {submitting ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}
